import { GenerateWhatsAppLinkAction } from './../../../../../actions/generate-whatsapp-link/generate-whatsapp-link.action';
import { QueryParamsResource } from './../../../../../infra/query-params/query-params.resource';
import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, signal, computed, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { AnotaButtonModule, AnotaIconModule } from '@githubanotaai/design-system';
import { ScreenTypeStore } from '@app/store/screen-type';
import { Router } from '@angular/router';
import { InformationsForm } from '../informations/form/informations-form.form';
import { EventTrackerAction } from '@actions/event-tracker';
import {
  MiddlewareService,
  MiddlewareExitIntentVariant,
  MiddlewareLayoutVariant,
} from './middleware.service';
import { ExitIntentAction } from '@actions/exit-intent';
import { MiddlewareExitIntentAction } from '@actions/middleware-exit-intent';
import { MiddlewareExitIntentModalComponent } from './exit-intent-modal/exit-intent-modal.component';
import { takeUntil, Subject, interval, debounceTime } from 'rxjs';
import { AmplitudeExperimentAction } from '@actions/amplitde-experiment/amplitde-experiment.action';

export interface MiddlewareVariantContent {
  title: string;
  ctaText: string;
  alternativeText: string;
  features: string[];
  discount?: string;
  originalPrice?: string;
  newPrice?: string;
  period?: string;
  method?: string;
  savedAmount?: string;
}

@Component({
  selector: 'app-middleware',
  standalone: true,
  imports: [
    CommonModule,
    AnotaButtonModule,
    ReactiveFormsModule,
    AnotaIconModule,
    MiddlewareExitIntentModalComponent
  ],
  templateUrl: './middleware.component.html',
  styleUrl: './middleware.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiddlewareComponent implements OnInit, OnDestroy {
  protected screenType = this.screenTypeStore.getScreenType();
  protected showExitIntentModal = signal(false);
  protected exitIntentVariant = signal<MiddlewareExitIntentVariant>('control');
  protected layoutVariant = signal<MiddlewareLayoutVariant>('control');
  protected variantLoaded = signal(false);
  protected offersRemaining = signal(13);
  protected isLoading = signal(false);

  deal_id: string = '';
  private destroy$ = new Subject<void>();
  private hireSubject$ = new Subject<void>();
  private exitIntentController: any;
  private readonly OFFERS_STORAGE_KEY = 'middleware_offers_remaining';
  private readonly OFFERS_TIMESTAMP_KEY = 'middleware_offers_timestamp';

  // Mapeamento de eventos por variante do exit intent
  private readonly exitIntentVariantEvents = {
    variantB: {
      webview: 'webview_modal_variant_b',
      close: 'click_close_variant_b',
      accept: 'click_liberar_acesso_7_dias_variant_b',
    },
  };

  private readonly defaultEvents = {
    webview: 'webview_signup_middleware',
    hire: 'signup_middleware_hire_now',
    try: 'signup_middleware_test_now',
  };

  protected discount = signal(0);
  protected price = signal('');
  protected period = signal(0);
  protected savedAmount = signal('');

  protected offersActivated = signal(false);

  // Configurações das variantes de layout
  protected readonly layoutVariants: Signal<Record<'control' | 'variantB', MiddlewareVariantContent>> = computed(() => {
    return {
      control: {
        title: 'Tudo que você precisa para automatizar, crescer e lucrar mais...',
        ctaText: 'Aproveitar oferta agora',
        alternativeText: 'Perder a oferta e continuar',
        features: [
          'Suporte de especialistas para ajudar a configurar sua loja;',
          'Valor do plano acompanha a quantidade de pedidos da loja;',
          'Oferta exclusiva para agora. Se pular essa etapa, perde a oferta.',
        ],
        discount: `${this.discount()}% OFF 🔥`,
        originalPrice: 'De R$ 299,99',
        newPrice: `R$ ${this.price()}`,
        period: this.period() > 1 ? `nos ${this.period()} primeiros meses` : 'no primeiro mês',
        savedAmount: `R$ ${this.savedAmount()}`
      },
      variantB: {
        title: 'Tudo que você precisa para automatizar, crescer e lucrar mais...',
        ctaText: 'Aproveitar oferta agora',
        alternativeText: 'Perder a oferta e testar grátis',
        features: [
          'Gestor de pedidos',
          'Cardápio Digital',
          'Robô de atendimento',
          'App para garçom',
          'Frente de caixa',
          'Cashback',
          'e muito mais...',
        ],
        discount: '',
        originalPrice: 'R$ 299,99',
        newPrice: `${this.price()}`,
        period: this.period() > 1 ? `nos ${this.period()} primeiros meses` : 'no primeiro mês'
      },
    };
  });

  protected readonly warningBannerItems = computed(() => Array(20).fill({
    text: this.offersActivated() ? 'ÚLTIMOS DIAS' : 'OFERTA EXCLUSIVA',
    icon: 'clock',
    iconColor: this.offersActivated() ? '#FFFFFF' : '#2B2B2B',
    iconSize: this.screenType() === 'mobile' ? '12px' : '17px'
  }));

  protected priceParts = computed(() => {
    const [units, cents] = this.price().split(',');

    return {
      units,
      cents: cents ?? '00'
    };
  });

  get currentLayoutVariant(): MiddlewareVariantContent {
    const variant = this.layoutVariant();
    return this.layoutVariants()[variant];
  }

  constructor(
    private screenTypeStore: ScreenTypeStore,
    private router: Router,
    private informationsForm: InformationsForm,
    private eventTrackerAction: EventTrackerAction,
    private middlewareService: MiddlewareService,
    private exitIntentAction: ExitIntentAction,
    private middlewareExitIntentAction: MiddlewareExitIntentAction,
    private amplitudeExperiment: AmplitudeExperimentAction,
    private queryParamsResource: QueryParamsResource,
    private generateWhatsAppLinkAction: GenerateWhatsAppLinkAction,
  ) {}

  async ngOnInit(): Promise<void> {
    this.eventTrackerAction.run('amplitude', 'webview_signup_middleware');
    this.deal_id = this.informationsForm?.values?.deal_id || '';
    this.middlewareService.systemUsage('webview_signup_middleware', this.deal_id).subscribe(res => {});

    await this.setupLayoutType();

    this.setupFlags();
    this.initializeOffersCounter();
    this.setupVariants();
    this.setupExitIntent();
    this.setupVisibilityListener();
    this.setupHireDebounce();
  }

  ngOnDestroy(): void {
    if (this.exitIntentController) {
      this.exitIntentController.stopListening();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async setupLayoutType(): Promise<void> {
    const offersActivatedFlag = await this.amplitudeExperiment.getFeatureFlagByKey('plan-offers-middleware') || 'off';
    this.offersActivated.set(offersActivatedFlag === 'on');
  }

  private async setupFlags() {
    const discountFlag = await this.amplitudeExperiment.getFeatureFlagByKey('value_discount_percentage') || '82';
    const priceFlag = await this.amplitudeExperiment.getFeatureFlagByKey('value_with_discount') || '49.99';
    const periodFlag = await this.amplitudeExperiment.getFeatureFlagByKey('value_month') || '3';
    const savedAmountFlag = await this.amplitudeExperiment.getFeatureFlagByKey('value_saved_amount') || '660';

    this.discount.set(Number(discountFlag?.replace(/\D/g, '')));
    this.price.set((Number(priceFlag?.replace(/\D/g, '')) / 100).toFixed(2).replace('.', ','));
    this.period.set(Number(periodFlag?.replace(/\D/g, '')));
    this.savedAmount.set((Number(savedAmountFlag?.replace(/\D/g, '')) / 100).toFixed(2).replace('.', ','));
  }

  private async setupVariants(): Promise<void> {
   this.layoutVariant.set('control');
      this.variantLoaded.set(true);
  }

  private initializeOffersCounter(): void {
    const storedOffers = localStorage.getItem(this.OFFERS_STORAGE_KEY);
    const storedTimestamp = localStorage.getItem(this.OFFERS_TIMESTAMP_KEY);

    if (storedOffers && storedTimestamp) {
      const currentTime = Date.now();
      const lastUpdate = parseInt(storedTimestamp);
      const timePassed = Math.floor((currentTime - lastUpdate) / (1000 * 60));

      let currentOffers = parseInt(storedOffers);

      currentOffers = Math.max(1, currentOffers - timePassed);

      this.offersRemaining.set(currentOffers);

      localStorage.setItem(this.OFFERS_TIMESTAMP_KEY, currentTime.toString());
      localStorage.setItem(this.OFFERS_STORAGE_KEY, currentOffers.toString());
    } else {
      this.offersRemaining.set(13);
      const currentTime = Date.now();
      localStorage.setItem(this.OFFERS_STORAGE_KEY, '13');
      localStorage.setItem(this.OFFERS_TIMESTAMP_KEY, currentTime.toString());
    }

    this.startOffersTimer();
  }

  private startOffersTimer(): void {
    interval(60000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const currentOffers = this.offersRemaining();
        if (currentOffers > 1) {
          const newOffers = currentOffers - 1;
          this.offersRemaining.set(newOffers);

          const currentTime = Date.now();
          localStorage.setItem(this.OFFERS_STORAGE_KEY, newOffers.toString());
          localStorage.setItem(this.OFFERS_TIMESTAMP_KEY, currentTime.toString());
        }
      });
  }

  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateOffersFromStorage();
      }
    });

    window.addEventListener('focus', () => {
      this.updateOffersFromStorage();
    });
  }

  private updateOffersFromStorage(): void {
    const storedOffers = localStorage.getItem(this.OFFERS_STORAGE_KEY);
    const storedTimestamp = localStorage.getItem(this.OFFERS_TIMESTAMP_KEY);

    if (storedOffers && storedTimestamp) {
      const currentTime = Date.now();
      const lastUpdate = parseInt(storedTimestamp);
      const timePassed = Math.floor((currentTime - lastUpdate) / (1000 * 60));

      let currentOffers = parseInt(storedOffers);

      if (timePassed > 0) {
        currentOffers = Math.max(1, currentOffers - timePassed);

        this.offersRemaining.set(currentOffers);
        localStorage.setItem(this.OFFERS_STORAGE_KEY, currentOffers.toString());
        localStorage.setItem(this.OFFERS_TIMESTAMP_KEY, currentTime.toString());
      }
    }
  }

  private setupHireDebounce(): void {
    this.hireSubject$
      .pipe(
        debounceTime(2000),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isLoading.set(false);
        const use_staging = /(localhost|staging|preprod)/.test(location.hostname);
        const link = use_staging ? 'https://staging-new-payment-system.anota-ai.com/checkout/' : 'https://pagamento.anota.ai/checkout/'

        const query = this.queryParamsResource.getCurrent();
        const from_contact = query?.['from_contact'];

        const redirectLink = from_contact === 'true'
          ? `${link + this.deal_id}/?self_source=LIVECHAT_SELF_CHECKOUT&utm_source=LIVECHAT_FORM`
          : `${link + this.deal_id}/?self_source=LP_SELF_CHECKOUT`;

        window.open(redirectLink, "_self")
      });
  }


  private trackExitIntentVariantEvent(
    variant: MiddlewareExitIntentVariant,
    eventType: keyof typeof this.exitIntentVariantEvents.variantB,
  ): void {
    if (variant === 'control' || !this.exitIntentVariantEvents[variant]) return;

    const eventName = this.exitIntentVariantEvents[variant][eventType];
    this.eventTrackerAction.run('amplitude', eventName, { variant });
  }

  private async setupExitIntent(): Promise<void> {
    try {
      const variant = await this.middlewareService.getExitIntentVariant();
      this.exitIntentVariant.set(variant);

      const query = this.queryParamsResource.getCurrent();

      if (query?.['from_contact'] === 'true' || variant === 'control') {
        return;
      }

      this.exitIntentController = this.exitIntentAction.run({ sensitivity: 15 });

      this.exitIntentController.onExitIntent$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.handleExitIntent();
      });

      this.exitIntentController.startListening();
    } catch (error) {
      this.exitIntentVariant.set('control');
    }
  }

  private handleExitIntent(): void {
    const variant = this.exitIntentVariant();

    if (variant === 'control' || this.showExitIntentModal()) {
      return;
    }

    this.eventTrackerAction.run('amplitude', 'middleware_exit_intent_triggered', {
      variant: variant,
    });

    this.middlewareExitIntentAction
      .run({
        variant: variant,
        dealId: this.deal_id,
      })
      .subscribe(() => {
        this.showExitIntentModal.set(true);

        this.trackExitIntentVariantEvent(variant, 'webview');
      });
  }

  protected onExitIntentAccept(): void {
    const variant = this.exitIntentVariant();

    this.trackExitIntentVariantEvent(variant, 'accept');

    this.eventTrackerAction.run('amplitude', 'middleware_exit_intent_accepted', {
      variant: variant,
    });

    this.try();
  }

  protected onExitIntentDismiss(): void {
    const variant = this.exitIntentVariant();
    this.trackExitIntentVariantEvent(variant, 'close');

    this.eventTrackerAction.run('amplitude', 'middleware_exit_intent_dismissed', {
      variant: variant,
    });

    this.showExitIntentModal.set(false);
  }

  hire() {
    this.middlewareService.systemUsage('signup_middleware_hire_now', this.deal_id).subscribe(res => {
      this.eventTrackerAction.run('amplitude', 'signup_middleware_hire_now');
    })

    this.isLoading.set(true);
    this.hireSubject$.next();
  }

  try() {
    this.middlewareService.systemUsage('signup_middleware_test_now', this.deal_id).subscribe(res => {
      this.eventTrackerAction.run('amplitude', 'signup_middleware_test_now');
    });

    const query = this.queryParamsResource.getCurrent();

    const from_contact = query?.['from_contact'];
    const phone = query?.['phone'];
    const message = 'Olá, gostaria de obter mais informações sobre a Anota AI.';

    if (from_contact === 'true') {
        const link = this.generateWhatsAppLinkAction.run({ phone, message });
        window.open(link, "_self");
        return;
    }

    this.router.navigate(['steps', 'credentials']);
  }
}
